import { screens } from '@change/ds-tokens/screens';

type ImagePreloadBreakpoint = keyof typeof screens;

export type ImagePreloadLinkEntry = {
	/**
	 * Image URL(s) to preload.
	 *
	 * - `string` — single preload link, no media query
	 * - `{ default, sm?, md?, lg?, xl? }` — responsive, mobile-first preload links.
	 *
	 *   Works like CSS/Tailwind breakpoints: each key applies from that breakpoint **up**
	 *   until the next specified breakpoint overrides it.
	 *
	 *   @example
	 *   // `default` → used below `md` (i.e. `max-width: md - 1`)
	 *   // `md` → used from `md` and up (i.e. `min-width: md`), since no `lg`/`xl` overrides
	 *   { default: 'mobile.jpg', md: 'desktop.jpg' }
	 *
	 *   @example
	 *   // `default` → below `sm`
	 *   // `sm` → from `sm` up to `lg - 1`
	 *   // `lg` → from `lg` and up
	 *   { default: 'small.jpg', sm: 'medium.jpg', lg: 'large.jpg' }
	 */
	href: string | (Partial<Record<ImagePreloadBreakpoint, string>> & { default: string });
	fetchPriority?: 'high' | 'low' | 'auto';
};

export type ResolvedImagePreloadLink = {
	href: string;
	media?: string;
	fetchPriority?: 'high' | 'low' | 'auto';
};

const BREAKPOINT_ORDER = ['sm', 'md', 'lg', 'xl'] as const;

function toPx(value: number): string {
	return `${value}px`;
}

function resolveEntry(entry: ImagePreloadLinkEntry): ResolvedImagePreloadLink[] {
	if (typeof entry.href === 'string') {
		return [{ href: entry.href, ...(entry.fetchPriority && { fetchPriority: entry.fetchPriority }) }];
	}

	const responsiveHref = entry.href;

	// Collect defined keys in order: default, sm, md, lg, xl
	const definedKeys: Array<{ key: 'default' | (typeof BREAKPOINT_ORDER)[number]; href: string }> = [];
	definedKeys.push({ key: 'default', href: responsiveHref.default });
	for (const bp of BREAKPOINT_ORDER) {
		if (responsiveHref[bp]) {
			definedKeys.push({ key: bp, href: responsiveHref[bp] });
		}
	}

	// If only default is defined, no media query needed
	if (definedKeys.length === 1) {
		return [{ href: definedKeys[0].href, ...(entry.fetchPriority && { fetchPriority: entry.fetchPriority }) }];
	}

	// Merge consecutive identical URLs
	const merged: Array<{ href: string; startKey: string }> = [];
	for (const { key, href } of definedKeys) {
		// .at(-1) (not [merged.length - 1]) so the type stays `T | undefined` when merged is empty —
		// bracket indexing doesn't add `| undefined` here (noUncheckedIndexedAccess is off).
		const last = merged.at(-1);
		if (last && last.href === href) {
			// Consecutive identical URLs — extend the previous range (no-op on startKey)
		} else {
			merged.push({ href, startKey: key });
		}
	}

	return merged.map(({ href, startKey }, idx) => {
		const isFirst = idx === 0;
		const isLast = idx === merged.length - 1;

		let media: string | undefined;
		if (isFirst && isLast) {
			media = undefined;
		} else if (isFirst) {
			const nextStartKey = merged[idx + 1].startKey;
			media = `(max-width: ${toPx(screens[nextStartKey as keyof typeof screens] - 1)})`;
		} else if (isLast) {
			media = `(min-width: ${toPx(screens[startKey as keyof typeof screens])})`;
		} else {
			const nextStartKey = merged[idx + 1].startKey;
			media = `(min-width: ${toPx(screens[startKey as keyof typeof screens])}) and (max-width: ${toPx(screens[nextStartKey as keyof typeof screens] - 1)})`;
		}

		return { href, ...(media && { media }), ...(entry.fetchPriority && { fetchPriority: entry.fetchPriority }) };
	});
}

export function resolveImagePreloadLinks(entries: ImagePreloadLinkEntry[]): ResolvedImagePreloadLink[] {
	return entries.flatMap(resolveEntry);
}
